import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Zap, Eye, EyeOff, Gift } from "lucide-react";

export default function AuthPage({ onAuth, apiClient }) {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState(searchParams.get("ref") || "");
  const [referrerName, setReferrerName] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (referralCode && !isLogin) {
      validateReferral(referralCode);
    }
  }, [referralCode, isLogin]);

  const validateReferral = async (code) => {
    try {
      const res = await apiClient.get(`/referral/validate/${code}`);
      if (res.data.valid) setReferrerName(res.data.referrer_name);
    } catch { setReferrerName(""); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin ? { email, password } : { name, email, password, referral_code: referralCode || undefined };
      const res = await apiClient.post(endpoint, payload);
      onAuth(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-violet-600/8 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-6 cursor-pointer" onClick={() => window.location.href = "/"}>
            <Zap className="w-7 h-7 text-violet-400" />
            <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>ContentForge</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-gray-500 text-sm">
            {isLogin ? "Sign in to continue generating" : "Start with 10 free credits"}
          </p>
        </div>

        <div className="glass rounded-xl p-8" data-testid="auth-form-container">
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-11 px-4 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-200"
                  placeholder="Your name"
                  required={!isLogin}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-11 px-4 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-200"
                placeholder="you@email.com"
                required
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-11 px-4 pr-11 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-200"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors duration-200">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5" data-testid="auth-error">
                {error}
              </div>
            )}

            {!isLogin && (
              <div>
                <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Referral Code <span className="normal-case text-gray-600">(optional)</span></label>
                <div className="relative">
                  <input
                    type="text"
                    value={referralCode}
                    onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); if (e.target.value.length >= 8) validateReferral(e.target.value); }}
                    className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-lg h-11 px-4 pr-11 text-sm text-white placeholder:text-gray-500 outline-none transition-colors duration-200"
                    placeholder="e.g. CF3XK9M2"
                    data-testid="auth-referral-input"
                  />
                  {referrerName && <Gift className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />}
                </div>
                {referrerName && (
                  <p className="text-xs text-emerald-400 mt-1.5" data-testid="referral-bonus-msg">
                    Referred by {referrerName} — you'll both get 5 bonus credits!
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white h-11 rounded-lg text-sm font-medium tracking-wide uppercase transition-all duration-200 shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isLogin ? "Signing in..." : "Creating account..."}
                </span>
              ) : (
                isLogin ? "Sign in" : "Create account"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              className="text-sm text-gray-400 hover:text-violet-400 transition-colors duration-200"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
